import { setValueValue } from '@databases/collab/y-database';
import {
  getOrderWithFallback,
  placeIdsInOrder,
  placeMappedIdsAfterSourceBlock
} from '@databases/model/database';
import { type DatabaseChangeType } from '@databases/types/redux';

import { getUniqueFieldName } from '../../../../field-name.ts';
import { type YJSChangeHandler } from '../../types.ts';

export const cloneFields: YJSChangeHandler<DatabaseChangeType.CLONE_FIELDS> = ({
  ydb,
  change
}) => {
  ydb.mutate((db) => {
    // Sort field IDs by their occurrence in the database and filter out non-existing ones
    const fieldIds = db.fields.filter((fieldId) =>
      Object.prototype.hasOwnProperty.call(change.fieldIds, fieldId)
    );

    const index = db.fields.indexOf(fieldIds[fieldIds.length - 1]) + 1;
    const fieldNames = db.fields.map((f) => db.fieldsById[f].name);
    const newFieldNames: string[] = [];
    let currentFieldIds = db.fields;

    for (let i = 0; i < fieldIds.length; i++) {
      const fieldId = fieldIds[i];
      const newFieldId = change.fieldIds[fieldId];
      const newFieldName = getUniqueFieldName(db.fieldsById[fieldId].name, [
        ...fieldNames,
        ...newFieldNames
      ]);

      newFieldNames.push(newFieldName);

      db.addFieldAt(
        newFieldId,
        {
          ...db.fieldsById[fieldId].toJSON(),
          name: newFieldName
        },
        index + i
      );

      currentFieldIds = [...currentFieldIds, newFieldId];

      db.views.forEach((viewId: string) => {
        if (db.viewsById[viewId] && db.viewsById[viewId].fields) {
          db.viewsById[viewId].setFieldVisibility(newFieldId, true, currentFieldIds, {
            includeNewFieldInValidation: true
          });
        }

        // copy quick calculations
        const calculation = db.viewsById[viewId].calculations[fieldId];

        if (calculation) {
          db.viewsById[viewId].setCalculation(newFieldId, calculation);
        }
      });

      for (const entry of [db.defaultEntry, ...Object.values(db.entriesById)]) {
        const value = entry?.getValue(fieldId);

        if (entry && value) {
          const newValue = entry.setValue(newFieldId, value.type);

          setValueValue(newValue, value.toJSON());
        }
      }
    }

    const placement = change.viewPlacement;
    if (placement) {
      db.views.forEach((viewId) => {
        const order = getOrderWithFallback(db.fields, db.viewOrders[viewId]?.fieldOrder);
        db.setViewFieldOrder(
          viewId,
          viewId === placement.currentViewId
            ? placeMappedIdsAfterSourceBlock(order, change.fieldIds)
            : placeIdsInOrder(order, Object.values(change.fieldIds), order.length)
        );
      });
    }
  });
};
